import { IconClose } from "@/assets/icons/geist/IconClose";
import type { TrackLocation } from "@/hooks/use-segment";
import * as Popover from "@radix-ui/react-popover";
import type { PopperContentProps } from "@radix-ui/react-popper";
import { cloneElement, useState, type FC, type ReactElement } from "react";

export const WithPopover: FC<{
    button: ReactElement;
    /**
     * An optional anchor to position the popover relative to. If omitted, the popover will be positioned relative to the trigger button.
     */
    anchor?: React.RefObject<HTMLElement>;
    children: ReactElement;
    hideCloseButton?: boolean;
    align?: PopperContentProps["align"];
    "data-track-location": TrackLocation;
}> = ({ button, children, anchor, "data-track-location": dataTrackLocation, hideCloseButton, align }) => {
    const [open, setOpen] = useState(false);

    return (
        <Popover.Root modal={true} open={open} onOpenChange={setOpen}>
            {anchor && (
                <Popover.Anchor asChild virtualRef={anchor} />
            )}
            <Popover.Trigger asChild type={undefined}>
                {button}
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content
                    side="bottom"
                    align={align}
                    sideOffset={10}
                    className="relative z-10 rounded-xl border-0.5 border-border-base bg-surface-pure shadow-xl backdrop-blur-sm data-[state=open]:data-[side=top]:animate-in data-[state=open]:data-[side=top]:fade-in data-[state=open]:data-[side=top]:zoom-in data-[state=open]:data-[side=top]:slide-in-from-bottom"
                    data-track-location={dataTrackLocation}
                >
                    {!hideCloseButton && (
                        <Popover.Close
                            className="absolute right-2 top-2 inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-md outline-none hover:bg-surface-tertiary"
                            aria-label="Close"
                        >
                            <IconClose size="sm" />
                        </Popover.Close>
                    )}
                    {cloneElement(children, { onClose: () => setOpen(false) })}
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
};
